import { RxJsonSchema, RxQuery, SyncOptions } from "rxdb";
import { Observable } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AppState } from "../../app-state";
import { AuthenticationService } from "../authentication.service";
import { TestbedRxdbCollectionService } from "./testbed-rxdb-collection.service";
import { TestbedRxdbState } from "./testbed-rxdb-state";

import PouchDB from "pouchdb";

export class TestbedRxdbCouchCollectionService<
  ModelType,
  RxDocType
> extends TestbedRxdbCollectionService<ModelType, RxDocType> {
  remote?: PouchDB.Database;
  syncBatchSize = 250;

  constructor(
    collectionName: string,
    schema: RxJsonSchema,
    appState: AppState,
    protected rxdbState: TestbedRxdbState,
    protected authService: AuthenticationService
  ) {
    super(collectionName, schema, appState, rxdbState);
  }

  buildSyncOptions() {
    let syncOpts: SyncOptions = {
      remote: this.remote,
      waitForLeadership: true,
      direction: { pull: this.syncConfig.pull, push: this.syncConfig.push },
      options: {
        batch_size: this.syncBatchSize,
        live: this.syncConfig.intervalMs == 0,
        retry: this.syncConfig.retry,
      },
    };

    if (this.syncConfig.query) {
      syncOpts.query = this.syncConfig.query(this.rxCollection) as RxQuery;
    }
    return syncOpts;
  }

  async startSync() {
    const syncConfig = this.syncConfig;

    if (!syncConfig?.name) {
      syncConfig.name = this.name;
    }

    if (!this.remote) {
      const urlPath = `${this.appState.settings.getSyncUrl()}/${
        syncConfig.name
      }`;
      this.remote = new PouchDB(urlPath, {
        fetch: (url, opts) => this.couchDbFetchFn(url, opts, this),
      });
      this.remote.setMaxListeners(TestbedRxdbCollectionService.MAX_LISTENERS);
    }

    this.bStopped = false;
    if (syncConfig.intervalMs == 0) {
      console.log("Starting live CouchDB sync " + this.name);
      this.doLiveSync();
    } else {
      console.log(
        "Starting nonLive CouchDB sync " +
          this.name +
          " every " +
          (syncConfig.intervalMs / 1000.0).toFixed(2) +
          " second(s)"
      );
      this.doNonLiveSync();
    }
  }

  doLiveSync = async () => {
    const syncConfig = this.syncConfig;
    let syncOpts = this.buildSyncOptions();

    if (syncConfig.query) {
      syncOpts.query = syncConfig.query(this.rxCollection) as RxQuery;
    }

    if (this.replicationState) {
      await this.replicationState.cancel();
      this.replicationStateListeners?.forEach((listener) => {
        listener?.replicationStateCanceled(this.replicationState);
      });
    }
    this.replicationState = this.rxCollection.sync(syncOpts);
    this.replicationStateListeners?.forEach((listener) => {
      listener?.replicationStateCreated(this.replicationState);
    });

    this.replicationState.active$.subscribe((bActive) => {
      const bLastActiveSync = this.bActiveSync;
      this.bActiveSync = bActive;
      if (
        bLastActiveSync &&
        !bActive &&
        !this.initialReplicationComplete$.value
      ) {
        this.initialReplicationComplete$.next(true);
      }
      this.replicationActive$.emit(bActive);
      this.rxdbState.processSyncActive(this, bActive);
    });

    this.replicationState.error$.subscribe((e) => {
      console.error('Live sync "' + this.name + '":' + e);
      this.error$.emit(e);
    });
  };

  async doNonLiveSync() {
    try {
      //console.log('doNonLiveSync(): ' + meta.rxCollection.name)
      var complete$: Observable<any> = null;
      var active$: Observable<any | boolean> = null;
      const syncOpts = this.buildSyncOptions();

      this.replicationState = this.rxCollection.sync(syncOpts as SyncOptions);
      complete$ = this.replicationState.complete$;
      active$ = this.replicationState.active$.pipe(
        takeUntil(this.replicationStateStale)
      );

      this.replicationStateListeners?.forEach((listener) => {
        listener?.replicationStateCreated(this.replicationState);
      });

      active$.subscribe((bActive) => {
        //console.log('setting ' + meta.rxCollection.name + '.bActiveSync from ' + meta.bActiveSync + ' to ' + bActive )
        this.replicationActive$.emit(bActive);
        this.rxdbState.processSyncActive(this, bActive);
      });

      complete$
        .pipe(takeUntil(this.replicationStateStale))
        .subscribe((bComplete) => {
          this.bActiveSync = false;
          if (bComplete && !this.initialReplicationComplete$.value) {
            this.initialReplicationComplete$.next(true);
          }
          this.rxdbState.processSyncComplete(this, bComplete);

          if (bComplete) {
            this.scheduleNonLiveSync(this.syncConfig.intervalMs);
          }
        });

      //*** Memory leak ****
      // if replication state gets error there is memory leak for PouchDb 'destroyed' listeners.
      // it can be addressed by adding this to node_modules/pouchdb-replication/lib/index.es.js at end of Replication.prototype.ready
      // self.once('error', cleanup);

      this.replicationState.error$
        .pipe(takeUntil(this.replicationStateStale))
        .subscribe((error) => {
          console.log("sync error for " + this.name);
          error?.innerErrors?.forEach((innerError) => {
            console.error(innerError.message);
          });
          this.error$.emit(error);
          this.bActiveSync = false;
          this.rxdbState.processSyncActive(this, false);
          const retryDelayMs = this.rxdbState.nextRetryDelayMs();
          console.log("next retry interval: " + retryDelayMs);
          this.scheduleNonLiveSync(retryDelayMs);
        });
    } catch (error) {
      console.error("doNonLiveSync() failed");
      console.error(error);
    }
  }

  syncNow() {
    if (this.syncConfig?.intervalMs > 0) {
      console.log("syncNow requested for " + this.name);
      let syncOpts = this.buildSyncOptions();
      const replicationState = this.rxCollection.sync(syncOpts as SyncOptions);
    } else {
      console.warn(
        `syncNow ignored for rxdb collection "${this.name}" because it is already using a live sync`
      );
    }
  }

  //
  // Custom PouchDB fetch function for CouchDB.  It addresses 2 issues:
  //
  // 1) CouchDB requires bearer token but api has no direct way to set it
  // 2) There is a memory leak in PouchDB error subscription for CouchDB and this fn lets us
  //    check every fetch without subscribing to errors. CouchDB live and non-live sync also report
  //    errors differently so this gives us a consistent way to know overall status of endpoints
  //
  couchDbFetchFn = (url, opts, meta) => {
    const dbFetchPromise = new Promise<Response>(
      async (completeFn, rejectFn) => {
        if (this.authService.currentUserValue) {
          let headers: Headers = opts.headers as Headers;
          headers.append(
            "Authorization",
            "Bearer " + this.authService.currentUserValue.token
          );
        }
        const oldOkCnt = this.rxdbState.getOkCount();
        try {
          const resp = await PouchDB.fetch(url, opts);
          meta.httpStatusCode = resp?.status;
          if (resp?.status < 200 || resp?.status >= 300) {
            console.warn(url + " (" + resp?.status + "): " + resp?.statusText);
            //this.scheduleNonLiveSync(meta, syncOpts, 5000)
          }
          completeFn(resp);
        } catch (error) {
          //console.warn('Fetch error for ' + url); // no connection, invalid auth token format, etc...
          if (meta.httpStatusCode === 401) {
            this.authService.logout();
            location.reload();
          }
          meta.httpStatusCode = -1;
          rejectFn(error);
        } finally {
          this.updateRxdbStateAfterHttpFetch(oldOkCnt);
        }
      }
    );
    return dbFetchPromise;
  };
}
